import React from 'react';
import PropTypes from 'prop-types';
import styled from 'react-emotion';
import { BoxDiv } from './box';
import classnames from 'classnames';
import Definition from './definition';
import SectionHeader from './section-header';
import { TextContent } from './text-content';
import { GapPageBreaker } from './gap';

const PrintOnlyGroupWrapper = styled(BoxDiv)`
  @media screen {
    & {
      display: none !important;
    }
  }
  .content {
    // cancel margin collapse
    overflow: auto;

    .picture {
      background-position: center;
      background-repeat: no-repeat;
    }
  }
`;

const DisplayOnlyGroupWrapper = styled(BoxDiv)`
  @media print {
    & {
      display: none !important;
    }
  }
  .header {
    height: 78px;
    display: flex;
    justify-content: space-around;
    align-items: stretch;
    // wechat web viewer doesn't support space-evenly so adding padding instead
    padding: 0;
    
    .tab {
      text-align: center;
      font-size: 29px;
      line-height: 78px;
      color: #6f6f6f;
      background: none;
      border: none;
      white-space: nowrap;
      
      &:hover,
      &:visited,
      &:focus
      {
        border: none;
        outline: none;
      }
      
      &.active {
        color: #22a7f0;
        border-bottom: 6px solid #22a7f0;
      }
    }
  }

  .content {
    // cancel margin collapse
    overflow: auto;
    
    border-top: 1px solid #f0f0f0

    .picture {
      margin: 30px 40px;
      width: calc(100% - 80px);
      min-height: 200px;
      background-position: center;
      background-repeat: no-repeat;
      
      &:last-child {
        margin-top: 40px;
        margin-bottom: 20px;
      }
    }
  }
`;

const PictureSection = ({
  src, height, fill, border, borderRadius
}) => (
  <div
    className='picture'
    style={{
      backgroundSize: `${fill}`,
      backgroundImage: `url(${src})`,
      height,
      border,
      borderRadius,
    }} />
);

PictureSection.defaultProps = {
  border: null,
  borderRadius: null,
  fill: 'cover',
};

PictureSection.propTypes = {
  src: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  border: PropTypes.string,
  borderRadius: PropTypes.string,
  fill: PropTypes.oneOf(['cover', 'contain']),
};

const SectionTitle = styled.div`
  padding: 0 40px;
  
  @media print {
    padding: 0;
  }
`;

const TitledSection = ({ title, subtitle, content }) => (
  <SectionTitle>
    <Definition name={title} explain={content} large={!subtitle} />
  </SectionTitle>
);

TitledSection.defaultProps = {
  subtitle: false,
  content: [],
};

TitledSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.bool,
  content: PropTypes.arrayOf(PropTypes.string),
};

const TabTitle = ({
  title, active, onClick, ...props
}) => (
  // eslint-disable-next-line max-len
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  <a
    className={classnames('tab', { active })}
    onClick={onClick}
    {...props}
  >{title}
  </a>
);

TabTitle.defaultProps = {
  active: false,
  onClick: null,
};

TabTitle.propTypes = {
  title: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

class TablePanel extends React.Component {
  componentDidMount() {
    const { onMount } = this.props;
    if (onMount) onMount(this);
  }

  render() {
    const { children } = this.props;
    return (
      <div className='content'>
        {children}
      </div>
    );
  }
}

TablePanel.defaultProps = {
  onMount: null,
  screenOnly: false,
};

TablePanel.propTypes = {
  // title is going to be used by the TabGroup
  // eslint-disable-next-line react/no-unused-prop-types
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onMount: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  screenOnly: PropTypes.bool,
};

const TabGroup = ({ active, children, onTabChange }) => (
  [
    <DisplayOnlyGroupWrapper key='display'>
      <div className='header'>
        {children.map((tab, idx) => (
          <TabTitle
            title={tab.props.title}
            onClick={() => (onTabChange ? onTabChange(idx) : null)}
            active={idx === active}
            key={tab.props.title} />
        ))}
      </div>
      {children[active]}
    </DisplayOnlyGroupWrapper>,

    <PrintOnlyGroupWrapper key='print'>
      {children.map((tab, idx) =>
        (tab.props.screenOnly ? null : [
          <SectionHeader title={tab.props.title} key={`${tab.props.title}-header`} />,
          <TextContent key={`${tab.props.title}-content`}>{tab}</TextContent>,
          idx < children.length - 1 ? <GapPageBreaker key={`${tab.props.title}-gap`} /> : null,
        ]))}
    </PrintOnlyGroupWrapper>
  ]
);

TabGroup.defaultProps = {
  children: null,
  onTabChange: null,
};

TabGroup.propTypes = {
  active: PropTypes.number.isRequired,
  children: PropTypes.node,
  onTabChange: PropTypes.func,
};

export default TabGroup;
export { TablePanel, PictureSection, TitledSection };



// WEBPACK FOOTER //
// ./src/components/tab-group.jsx